/* Het websitescherm (ADR 0034): één samenvatting uit website.overzicht(p_dagen) (migratie 110).
 * Bezoek, formulieren, inhoud, signalen en wat er laatst gebeurde. De client komt als argument
 * binnen, zodat de toetsen hem kunnen nabootsen. Hier wordt niets geschreven. */

export interface OverzichtClient {
  schema(naam: 'website'): { rpc(fn: string, args: Record<string, unknown>): PromiseLike<{ data: unknown; error: unknown }> };
}

export type Venster = 7 | 30 | 90;
export const VENSTERS: Venster[] = [7, 30, 90];

export interface BezoekDag { dag: string; weergaven: number; sessies: number }
export interface Bezoek {
  dagen: number;
  sessies: number;
  weergaven: number;
  sessies_vorig: number;
  weergaven_vorig: number;
  per_dag: BezoekDag[];
  paden: { pad: string; weergaven: number; sessies: number }[];
  herkomst: { bron: string; sessies: number }[];
  tools: { pad: string; weergaven: number }[];
}

export interface Formulier {
  id: string;
  created_at: string;
  stage: string;
  status: string;
  summary: string | null;
  is_test: boolean | null;
  soort: string | null;
  label: string | null;
  pagina: string | null;
  naam: string | null;
  bedrijf: string | null;
  onderwerp: string | null;
  email: string | null;
  eigenaar: string | null;
}
export interface Inzending { kind: string; created_at: string; vacancy_slug: string | null; is_test: boolean | null }
export interface Formulieren { leads: Formulier[]; nieuw: number; inzendingen: Inzending[]; laatste_7_dagen: number }

export interface InhoudStand { soort: string; aantal: number; concepten: number; laatst: string | null }

export interface Signalen {
  ververs_mislukt_24u: number;
  ververs_laatste: { uitkomst: string; reden: string | null; created_at: string } | null;
  leads_wachten: number;
  formulieren_zonder_lead: number;
  concepten: number;
}

export interface Gebeurtenis {
  wat: 'inhoud' | 'ververs';
  soort: string | null;
  titel: string | null;
  slug: string | null;
  op: string;
  uitkomst: string | null;
  reden: string | null;
}

export interface Overzicht {
  bezoek: Bezoek;
  formulieren: Formulieren;
  inhoud: InhoudStand[];
  signalen: Signalen;
  laatst: Gebeurtenis[];
  gemaakt_op: string;
}

/** Wat er mis kan zijn met het ophalen: de migratie is er nog niet, geen CRM-rol, of iets anders. */
export type FoutSoort = 'niet_ingericht' | 'geen_toegang' | 'anders';
export class OverzichtFout extends Error {
  soort: FoutSoort;
  constructor(soort: FoutSoort, bericht: string) {
    super(bericht);
    this.soort = soort;
  }
}

/** PGRST202 is de code van PostgREST voor een functie die er niet is: de migratie is nog niet toegepast. */
const leesFout = (fout: unknown): OverzichtFout => {
  const f = fout as { code?: string; message?: string } | null;
  const bericht = f?.message ?? 'onbekende fout';
  if (f?.code === 'PGRST202' || /Could not find the function/i.test(bericht)) return new OverzichtFout('niet_ingericht', bericht);
  if (f?.code === '42501' || /geen toegang/i.test(bericht)) return new OverzichtFout('geen_toegang', bericht);
  return new OverzichtFout('anders', bericht);
};

export async function haalOverzicht(client: OverzichtClient, dagen: Venster): Promise<Overzicht> {
  let antwoord: { data: unknown; error: unknown };
  try {
    antwoord = await client.schema('website').rpc('overzicht', { p_dagen: dagen });
  } catch (e) {
    throw new OverzichtFout('anders', (e as Error)?.message ?? 'geen verbinding');
  }
  if (antwoord.error) throw leesFout(antwoord.error);
  const d = antwoord.data as Partial<Overzicht> | null;
  if (!d || !d.bezoek || !d.formulieren) throw new OverzichtFout('anders', 'onverwacht antwoord');
  return {
    bezoek: d.bezoek,
    formulieren: d.formulieren,
    inhoud: d.inhoud ?? [],
    signalen: d.signalen ?? { ververs_mislukt_24u: 0, ververs_laatste: null, leads_wachten: 0, formulieren_zonder_lead: 0, concepten: 0 },
    laatst: d.laatst ?? [],
    gemaakt_op: d.gemaakt_op ?? new Date().toISOString(),
  };
}

/* ── rekenen en benoemen ──────────────────────────────────────────── */

/** Het verschil met de vorige periode in procenten, afgerond; null als er niets te vergelijken valt. */
export function verschilProcent(nu: number, vorig: number): number | null {
  if (!vorig) return null;
  return Math.round(((nu - vorig) / vorig) * 100);
}

export const HERKOMST_LABEL: Record<string, string> = {
  direct: 'Direct',
  google: 'Google',
  zoekmachine: 'Andere zoekmachine',
  linkedin: 'LinkedIn',
  social: 'Social',
  overig: 'Overig',
};
export const herkomstLabel = (bron: string): string => HERKOMST_LABEL[bron] ?? bron;

/** Het aandeel van elke bron in procenten, op het totaal van de getoonde bronnen. */
export function herkomstAandeel(herkomst: Bezoek['herkomst']): { bron: string; label: string; sessies: number; procent: number }[] {
  const totaal = herkomst.reduce((s, h) => s + h.sessies, 0);
  return herkomst.map((h) => ({ ...h, label: herkomstLabel(h.bron), procent: totaal ? Math.round((h.sessies / totaal) * 100) : 0 }));
}

export const TOOL_LABEL: Record<string, string> = {
  '/tools/ere-calculator': 'ERE-calculator',
  '/tools/diesel-vs-elektrisch': 'Diesel vs. elektrisch',
  '/tools': 'Overzicht rekentools',
};
export const toolLabel = (pad: string): string => TOOL_LABEL[pad] ?? pad.replace('/tools/', '');

/** Een lead "wacht" als hij nog op new staat; opgepakt is alles daarna. */
export const leadWacht = (f: Formulier): boolean => f.stage === 'new';

export const STADIUM_LABEL: Record<string, string> = {
  new: 'Nog niet opgepakt',
  contacted: 'Contact gelegd',
  qualified: 'Gekwalificeerd',
  proposal: 'Voorstel',
  negotiation: 'In onderhandeling',
  won: 'Gewonnen',
  lost: 'Verloren',
};
export const stadiumLabel = (stage: string): string => STADIUM_LABEL[stage] ?? stage;

export const INZENDING_LABEL: Record<string, string> = { sollicitatie: 'Sollicitatie', nieuwsbrief: 'Nieuwsbrief' };

/** Het aantal signalen dat aandacht vraagt (de rode tegel). Concepten horen daar niet bij: dat is werk, geen storing. */
export const aantalSignalen = (s: Signalen): number => s.ververs_mislukt_24u + s.leads_wachten + s.formulieren_zonder_lead;

export function signaalTeksten(s: Signalen): string[] {
  const uit: string[] = [];
  if (s.ververs_mislukt_24u > 0) uit.push(`${s.ververs_mislukt_24u}× verversen mislukt (24 uur)`);
  if (s.leads_wachten > 0) uit.push(`${s.leads_wachten} ${s.leads_wachten === 1 ? 'formulier wacht' : 'formulieren wachten'} al 2 dagen`);
  if (s.formulieren_zonder_lead > 0) uit.push(`${s.formulieren_zonder_lead} ${s.formulieren_zonder_lead === 1 ? 'aanvraag' : 'aanvragen'} zonder lead`);
  return uit;
}

/** "ververst 2 min geleden" of "ververs mislukt 13:48". null zonder logboek. */
export function verversTekst(s: Signalen, nu: Date = new Date()): string | null {
  const l = s.ververs_laatste;
  if (!l) return null;
  const min = Math.max(0, Math.round((nu.getTime() - new Date(l.created_at).getTime()) / 60_000));
  const wanneer = min < 1 ? 'zojuist' : min < 60 ? `${min} min geleden` : min < 48 * 60 ? `${Math.round(min / 60)} uur geleden` : `${Math.round(min / 1440)} dagen geleden`;
  if (l.uitkomst === 'gelukt') return `ververst ${wanneer}`;
  if (l.uitkomst === 'niet_ingesteld') return 'verversen nog niet ingesteld';
  return `verversen mislukt ${wanneer}${l.reden ? ` (${l.reden})` : ''}`;
}

/** Hoe lang geleden, kort: "zojuist", "3 u", "gisteren", "5 d". */
export function geledenKort(iso: string, nu: Date = new Date()): string {
  const min = Math.round((nu.getTime() - new Date(iso).getTime()) / 60_000);
  if (min < 1) return 'zojuist';
  if (min < 60) return `${min} min`;
  const uur = Math.round(min / 60);
  if (uur < 24) return `${uur} u`;
  const dag = Math.round(uur / 24);
  if (dag === 1) return 'gisteren';
  if (dag < 14) return `${dag} d`;
  return new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'short' }).format(new Date(iso)).replace('.', '');
}
